import { lazy } from 'react';
import SubscriptionPopupIsland from '@components/islands/SubscriptionPopup.island';
import TrialMonthlyRebillPopupIsland from '@components/islands/TrialMonthlyRebillPopup.island';
import RescuePopupIsland from '@components/islands/RescuePopup.island';
import AnnualPlanPopupIsland from '@components/islands/AnnualPlanPopup.island';
import PayAsYouGoPopupIsland from '@components/islands/PayAsYouGoPopup.island';
import { retryImport } from '@utils/retryImport';
import { IslandComponent, IslandName } from './types';

type IslandLoader = () => Promise<{ default: IslandComponent }>;

// Every island is its own async chunk (`static/js/island.<name>.<hash>.chunk.js`,
// see `chunkFilename` in webpack.widget.js), so a page only downloads the
// islands it actually hosts — the pricing block never pulls the seven funnel
// forms and vice versa. Each import goes through `retryImport`: the island has
// no chunk-error reload of its own.
const loaders: Record<
  Exclude<
    IslandName,
    | 'SubscriptionPopup'
    | 'TrialMonthlyRebillPopup'
    | 'RescuePopup'
    | 'AnnualPlanPopup'
    | 'PayAsYouGoPopup'
  >,
  IslandLoader
> = {
  PricingCards: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "pricing-cards" */ '@components/islands/PricingCards.island'
        )
    ),
  FunnelMultiTool: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "funnel-multi-tool" */ '@components/islands/FunnelMultiTool.island'
        )
    ),
  FunnelWordCounterTool: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "funnel-word-counter" */ '@components/islands/FunnelWordCounterTool.island'
        )
    ),
  FunnelAiDetectorHumanizeTool: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "funnel-ai-detector-humanize" */ '@components/islands/FunnelAiDetectorHumanizeTool.island'
        )
    ),
  FunnelAiHumanizeTool: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "funnel-ai-humanize" */ '@components/islands/FunnelAiHumanizeTool.island'
        )
    ),
  FunnelPlagiarismCheckerTool: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "funnel-plagiarism-checker" */ '@components/islands/FunnelPlagiarismCheckerTool.island'
        )
    ),
  FunnelAiDetectorTool: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "funnel-ai-detector" */ '@components/islands/FunnelAiDetectorTool.island'
        )
    ),
  FunnelAiCheckerTool: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "funnel-ai-checker" */ '@components/islands/FunnelAiCheckerTool.island'
        )
    ),
  ProfileHeader: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "profile-header" */ '@components/islands/ProfileHeader.island'
        )
    ),
  ProfileSidebar: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "profile-sidebar" */ '@components/islands/ProfileSidebar.island'
        )
    ),
  UpdatePaymentMethodNotice: () =>
    retryImport(
      () =>
        import(
          /* webpackChunkName: "update-payment-method-notice" */ '@components/islands/UpdatePaymentMethodNotice.island'
        )
    ),
};

// `SubscriptionPopup`, `TrialMonthlyRebillPopup`, `RescuePopup`, `AnnualPlanPopup`
// and `PayAsYouGoPopup` render nothing (they only give the bundle a host, see
// their `*.island.tsx`) — a chunk request for them would be pure overhead.
const staticIslands: Partial<Record<IslandName, IslandComponent>> = {
  SubscriptionPopup: SubscriptionPopupIsland,
  TrialMonthlyRebillPopup: TrialMonthlyRebillPopupIsland,
  RescuePopup: RescuePopupIsland,
  AnnualPlanPopup: AnnualPlanPopupIsland,
  PayAsYouGoPopup: PayAsYouGoPopupIsland,
};

// One `lazy()` per island name, created on first use and reused by every host
// of that island (and by later `mount()` calls), so the chunk is requested once.
const lazyIslands = new Map<IslandName, IslandComponent>();

const getLazyIsland = (name: IslandName): IslandComponent => {
  const cached = lazyIslands.get(name);
  if (cached) {
    return cached;
  }

  const island = lazy(loaders[name as keyof typeof loaders]);
  lazyIslands.set(name, island);

  return island;
};

/**
 * Resolves a `data-react-island` name to its component — a `React.lazy` one for
 * the code-split islands, so the host must render it under `Suspense`
 * (`IslandRoot` does). An own-property check, not a plain lookup: the name comes
 * from a `data-*` attribute, so `constructor` or `__proto__` would otherwise
 * resolve.
 */
export const getIsland = (name: string | null): IslandComponent | undefined => {
  if (!name) {
    return undefined;
  }

  if (Object.hasOwn(staticIslands, name)) {
    return staticIslands[name as IslandName];
  }

  if (Object.hasOwn(loaders, name)) {
    return getLazyIsland(name as IslandName);
  }

  return undefined;
};
